import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompaniesLegalEntity,
  FinanceCreateDimensionValueDocument,
  FinanceGetDimensionValuesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const useLegalEntityOptions = createComputed((legalEntities) => {
  return legalEntities.map((le: CompaniesLegalEntity) => {
    return {
      label: le.legalName,
      value: le.id,
    }
  })
})
interface NewDimensionValueModalProps {
  name: string
  onClose: (success?: boolean) => void
}

interface CreateDimensionValueFormValues {
  name: string
  code?: string
  description?: string
  legalEntityId: string
}

const NewDimensionValueModal = ({ name, onClose }: NewDimensionValueModalProps) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)

  const { id: dimensionId } = useParams()
  const { company } = useCurrentSession()

  const [createDimensionValue] = useMutation(FinanceCreateDimensionValueDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceCustomDimensionValue,
  })

  const form = useForm<CreateDimensionValueFormValues>({
    defaultValues: {
      name: '',
      legalEntityId: '',
    },

    validate: (data) => {
      const errors: FormErrors<CreateDimensionValueFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('finance.modals.add_dimension_value.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      setSubmitting(true)
      const variables = {
        name: values.name,
        code: values.code?.trim() || null,
        description: values.description?.trim() || null,
        customDimensionId: Number(dimensionId),
        tenant: {
          companyId: company.id,
          legalEntityIds: values.legalEntityId ? [Number(values.legalEntityId)] : null,
        },
      }

      const result = await createDimensionValue({
        variables,
        refetchQueries: [
          {
            query: FinanceGetDimensionValuesDocument,
            variables: {
              dimensionId: Number(dimensionId),
              tenant: { companyId: company.id },
            },
          },
        ],
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          name: errors['name'],
          code: errors['code'],
          description: errors['description'],
        })
        app.setFlash({ type: 'wrong', message: i18n.t('finance.modals.add_dimension_value.error') })
      })

      if (handled.success) {
        onClose(true)
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.modals.add_dimension_value.success'),
        })
      }
      setSubmitting(false)
      return handled
    },
  })

  const handleClose = (afterSubmit = false) => {
    if (!afterSubmit) {
      // trackCostCenterAction(trackingEvents.accounting.createCostCenterCancel)
    }
    onClose()
  }

  const legalEntityList = useLegalEntityOptions(company.legalEntities).get()

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('finance.modals.add_dimension_value.title', { dimension_name: name })}
      description={i18n.t('finance.modals.add_dimension_value.description')}
      onClose={handleClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={submitting || !form.values.name.length} submit>
          {i18n.t('finance.buttons.add_dimension_value')}
        </Button>
      }
    >
      <Box paddingBottom="s24">
        <TextField
          mandatory
          label={i18n.t('finance.modals.add_dimension_value.fields.name.label')}
          defaultValue={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
        />
      </Box>
      <Box paddingBottom="s24">
        <SelectField
          label={i18n.t('finance.modals.add_dimension_value.fields.legal_entity.label')}
          defaultValue={form.values.legalEntityId}
          options={legalEntityList}
          onChange={(value) => form.setValues({ ...form.values, legalEntityId: value })}
          defaultOption={{
            label: i18n.t('finance.modals.add_account.fields.legal_entity.options.all'),
            value: false,
          }}
        />
      </Box>
      <Box paddingBottom="s24">
        <TextField
          label={i18n.t('finance.modals.add_dimension_value.fields.code.label')}
          defaultValue={form.values.code}
          onChange={(value) => form.setValues({ ...form.values, code: value })}
          error={form.errors.code}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('finance.modals.add_dimension_value.fields.code.information')}
              icon="question"
            />
          }
        />
      </Box>
      <Box paddingBottom="s24">
        <TextField
          label={i18n.t('finance.modals.add_dimension_value.fields.description.label')}
          defaultValue={form.values.description}
          onChange={(value) => form.setValues({ ...form.values, description: value })}
          error={form.errors.description}
        />
      </Box>
    </Modal>
  )
}

export default observer(NewDimensionValueModal)
